import * as XLSX from 'xlsx';
import { TelstraMobileLineItem, TelstraMobileReport } from '../services/telstraService';

// Helper to get column index from letter (A=0, B=1, etc) or return number as is
const COL_IDX = (s: string | number): number => {
  if (typeof s === 'number') return s;
  let n = 0; 
  for (let i=0; i<s.length; i++){ 
    n = n*26 + (s.charCodeAt(i)-64);
  } 
  return n-1; 
};

const getCell = (row: any[], idx: number) => row[idx];
// Parse money value - returns absolute value (backend rejects negative numbers due to [Range(0, ...)] constraint)
const money = (v: any) => { 
  if (v==null) return 0; 
  const s = String(v).trim().replace(/[$,\s]/g,''); 
  const num = Number(s.replace(/[()]/g,'')) || 0; 
  return Math.abs(num); // Always return positive - backend has Range(0, 1000000) constraint
};
const numberMb = (v: any) => { if (v==null || v==='') return 0; const s=String(v).trim(); const m=s.match(/([\d,.]+)\s*(GB|MB)?/i); if(!m) return Number(s) || 0; const val=Number(m[1].replace(/,/g,''))||0; const unit=(m[2]||'MB').toUpperCase(); return unit==='GB'? val*1024 : val; };
// Parse integer - ensure non-negative for backend Range constraints
const toInt = (v: any) => { const n = Number(String(v).replace(/[,\s]/g,'')); return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0; };
const normStr = (v: any) => String(v ?? '').trim();
const normalizeBarStatus = (v: any): string | undefined => {
  const raw = normStr(v).toUpperCase();
  if (!raw) return undefined;
  if (['YES', 'Y', 'TRUE', '1', 'BAR', 'BARRED', 'BLOCKED'].includes(raw)) return 'YES';
  if (['NO', 'N', 'FALSE', '0', 'UNBARRED', 'ACTIVE', 'ALLOWED'].includes(raw)) return 'NO';
  return raw;
};
const toFloat = (v: any) => {
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const toDateString = (v: any): string | undefined => {
  if (v == null || v === '') return undefined;
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    const parsed = XLSX.SSF.parse_date_code(v);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }
  const s = normStr(v);
  const au = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (au) {
    const day = au[1].padStart(2, '0');
    const month = au[2].padStart(2, '0');
    const year = au[3].length === 2 ? `20${au[3]}` : au[3];
    return `${year}-${month}-${day}`;
  }
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  }
  return s || undefined;
};
const monthLabelToStatementMonth = (label: string | undefined): string | undefined => {
  const match = normStr(label).match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})$/i);
  if (!match) return undefined;
  const month = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
    .indexOf(match[1].toLowerCase()) + 1;
  if (month <= 0) return undefined;
  return `${match[2]}-${String(month).padStart(2, '0')}`;
};
// Truncate string to max length to match backend StringLength constraints
const truncStr = (v: any, maxLen: number) => { const s = normStr(v); return s.length > maxLen ? s.slice(0, maxLen) : s; };
const looksLikeAuMobile = (s: string) => /^(\+?61|0)4\d{8}$/.test(s.replace(/\s/g,''));

// Default column mapping (fallback)
const DEFAULT_COLS = {
  customerName: 'D',
  accountNumber: 'H',
  mobileNumber: 'I',
  userName: 'J',
  serviceActivationDate: 'K',
  planName: 'R',
  monthlyFee: 'V',
  includedData: 'CU',
  simNumber: 'AA',
  deviceImei: 'AE',
  deviceMake: 'AF',
  deviceModelNumber: 'AG', // Model number (less useful for display)
  deviceName: 'AH', // Device Name (user-friendly name)
  deviceType: 'AI',
  deviceCategory: 'AJ',
  deviceOperatingSystem: 'AK',
  deviceAgeMonths: 'OQ',
  latestMonthDataMb: 'FJ',
  latestMonthVoiceCalls: 'ID',
  latestMonthVoiceMinutes: 'IP',
  latestMonthSms: 'JN',
  latestMonthBillTotal: 'JZ',
  avg3mDataMb: 'KL',
  avg3mVoiceMinutes: 'KM',
  avg3mBill: 'KP',
  avg6mDataMb: 'KN',
  avg6mVoiceMinutes: 'KO',
  avg6mBill: 'KP',
  deviceTradeInValue: 'OE', // Device Trade In Indicative Value inc GST
  barRoaming: 'LQ',
  barIdd: 'LR',
  // IR (International Roaming)
  irDataPackCode: 'CV',
  irDataPackDescription: 'CW',
  irDataPackMonthlyFee: 'CX',
  irVoicePackCode: 'CZ',
  irVoicePackDescription: 'DA',
  irVoicePackMonthlyFee: 'DB',
  irActive: 'KQ',
  avg3mIrDataMb: 'MG',
  // IDD (International Direct Dialing)
  iddPackIndicator: 'MW',
  iddPackCode: 'MX',
  iddPackDescription: 'MY',
  iddPackMonthlyFee: 'MZ',
  avg3mIddExcess: 'NA',
  avg6mIddExcess: 'NB',
  // Stale service flags (N/Y)
  noUsage3m: 'ML',
  noUsage6m: 'MM',
  noNetworkActivity3m: 'NH',
  noNetworkActivity6m: 'NI',
  onlyDiversionUsage3m: 'OG',
  onlyDiversionUsage6m: 'OH',
  onlyMessagebankDiversionUsage3m: 'OL',
  onlyMessagebankDiversionUsage6m: 'ON',
  // Location
  mostUsedSite: 'MI',
  mostUsedPostcode: 'MJ',
  // Monthly series start columns (12 consecutive columns, newest first)
  monthlyDataMbStart: 'FJ',       // FJ-FU: Domestic Data Usage (MB) x12
  monthlyVoiceCallsStart: 'ID',   // ID-IO: Domestic Voice Calls x12
  monthlyVoiceMinutesStart: 'IP', // IP-JA: Domestic Voice Minutes x12
  monthlySmsStart: 'JN',          // JN-JY: Domestic SMS x12
  monthlyBillTotalStart: 'JZ',    // JZ-KK: Bill Total x12
  monthlyOverseasDataCallsStart: 'GH',   // GH-GS: Overseas Data Calls x12
  monthlyOverseasDataMinutesStart: 'GT', // GT-HE: Overseas Data Minutes x12
  monthlyOverseasDataMbStart: 'HF',      // HF-HQ: Overseas Data Usage (MB) x12
  monthlyOverseasDataExcessStart: 'HR',  // HR-IC: Overseas Data Excess Usage ($) x12
};

/** Customers billed on Telstra's digital platform ("Digi" customers) receive Blitz 2.0
 * workbooks with an empty User Name column. When no row has a user name, every row is
 * stamped N/A so the gap is obvious. Core applies the same rule server-side. */
export const MISSING_USER_NAME = 'N/A';

// Header keywords to search for (lowercase)
const HEADER_KEYWORDS: Record<keyof typeof DEFAULT_COLS, string[]> = {
  customerName: ['customer name', 'customer', 'business name', 'company name', 'organisation name', 'organization name'],
  accountNumber: ['account number', 'account'],
  mobileNumber: ['service number', 'mobile number', 'phone number'],
  userName: ['user name', 'username', 'cost centre', 'cost center', 'assigned to'],
  serviceActivationDate: ['service activation date'],
  planName: ['plan name', 'plan'],
  monthlyFee: ['monthly fee', 'access fee', 'plan cost', 'monthly charge'],
  includedData: ['total data allowance', 'total data allowance (mb)', 'data allowance (mb)', 'data allowance', 'included data', 'data included'],
  simNumber: ['sim number', 'sim'],
  deviceImei: ['imei', 'device imei'],
  deviceMake: ['make', 'device make'],
  deviceModelNumber: ['model', 'model number', 'model no', 'model#'],
  deviceName: ['device name', 'device description', 'handset name', 'handset', 'device model'],
  deviceType: ['device type', 'asset type'],
  deviceCategory: ['device category'],
  deviceOperatingSystem: ['operating system', 'device operating system'],
  deviceAgeMonths: ['device age - service level', 'device age'],
  latestMonthDataMb: ['total data (mb)', 'data usage', 'data volume', 'data used', 'monthly data', 'current month data'],
  latestMonthVoiceCalls: ['voice calls', 'total calls', 'domestic voice calls'],
  latestMonthVoiceMinutes: ['voice minutes', 'total minutes', 'voice duration', 'domestic voice minutes', 'minutes of use'],
  latestMonthSms: ['sms', 'total sms', 'messages'],
  latestMonthBillTotal: ['total charges', 'total bill', 'total spend', 'total cost'],
  avg3mDataMb: ['avg data', '3 month avg data', 'average 3 month domestic data'],
  avg3mVoiceMinutes: ['avg voice', '3 month avg voice', 'average 3 month domestic voice'],
  avg3mBill: ['avg bill', '3 month avg bill', 'average spend', 'average 3 month bill'],
  avg6mDataMb: ['6 month avg data', 'average 6 month domestic data', 'average 6 month domestic data (mb)'],
  avg6mVoiceMinutes: ['6 month avg voice', 'average 6 month domestic voice', 'average 6 month domestic voice minutes of use'],
  avg6mBill: ['6 month avg bill', 'average 6 month bill', 'average 6 month spend'],
  deviceTradeInValue: ['device trade in', 'trade in value', 'trade-in value', 'trade in indicative value'],
  barRoaming: ['bar roaming'],
  barIdd: ['bar idd', 'idd bar', 'international direct dialing', 'bar international direct dialing'],
  // IR (International Roaming)
  irDataPackCode: ['ir data pack'],
  irDataPackDescription: ['ir data pack description'],
  irDataPackMonthlyFee: ['ir data pack monthly commitment', 'ir data pack monthly fee'],
  irVoicePackCode: ['ir voice pack'],
  irVoicePackDescription: ['ir voice pack description'],
  irVoicePackMonthlyFee: ['ir voice pack monthly commitment', 'ir voice pack monthly fee'],
  irActive: ['international roaming active', 'ir active'],
  avg3mIrDataMb: ['average 3 month ir data', 'avg 3 month ir data'],
  // IDD (International Direct Dialing)
  iddPackIndicator: ['idd pack indicator'],
  iddPackCode: ['latest idd pack'],
  iddPackDescription: ['latest idd pack description', 'idd pack description'],
  iddPackMonthlyFee: ['latest idd pack monthly fee', 'idd pack monthly fee'],
  avg3mIddExcess: ['average 3 month idd excess', 'avg 3 month idd excess'],
  avg6mIddExcess: ['average 6 month idd excess', 'avg 6 month idd excess'],
  // Stale service flags
  noUsage3m: ['no usage in last 3 months', 'no usage 3 months'],
  noUsage6m: ['no usage in last 6 months', 'no usage 6 months'],
  noNetworkActivity3m: ['no network activity in last 3 months', 'no network activity 3 months', 'no network activity (3 months)'],
  noNetworkActivity6m: ['no network activity in last 6 months', 'no network activity 6 months', 'no network activity (6 months)'],
  onlyDiversionUsage3m: ['only diversion usage in last 3 months'],
  onlyDiversionUsage6m: ['only diversion usage in last 6 months'],
  onlyMessagebankDiversionUsage3m: ['only messagebank diversion usage in last 3 months'],
  onlyMessagebankDiversionUsage6m: ['only messagebank diversion usage in last 6 months'],
  // Location
  mostUsedSite: ['most used site', 'last 3 months most used site'],
  mostUsedPostcode: ['most used postcode', 'last 3 months most used postcode'],
  // Monthly series (matched by first column header to find start position)
  monthlyDataMbStart: ['domestic data usage'],
  monthlyVoiceCallsStart: ['domestic voice calls'],
  monthlyVoiceMinutesStart: ['domestic voice minutes'],
  monthlySmsStart: ['domestic sms'],
  monthlyBillTotalStart: ['bill total'],
  monthlyOverseasDataCallsStart: ['overseas data calls'],
  monthlyOverseasDataMinutesStart: ['overseas data minutes'],
  monthlyOverseasDataMbStart: ['overseas data usage'],
  monthlyOverseasDataExcessStart: ['overseas data excess usage'],
};

export async function parseTelstraMobileXlsxFile(
  file: File,
  tenantId: string,
  options?: { sheetName?: string; sheetIndex?: number }
): Promise<{ report: TelstraMobileReport; warnings: string[] }> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const warnings: string[] = [];

  const sheetNames = wb.SheetNames || [];
  let targetSheetName: string | undefined;
  if (options?.sheetName) {
    const searchName = options.sheetName.trim();
    
    // 1. Try exact match
    targetSheetName = sheetNames.find(n => n === searchName);

    // 2. Try case-insensitive match
    if (!targetSheetName) {
      targetSheetName = sheetNames.find(n => n.toLowerCase() === searchName.toLowerCase());
    }

    // 3. Try trimmed case-insensitive match (handles sheets with extra whitespace)
    if (!targetSheetName) {
      targetSheetName = sheetNames.find(n => n.trim().toLowerCase() === searchName.toLowerCase());
    }

    // 4. Try partial/contains match
    if (!targetSheetName) {
      targetSheetName = sheetNames.find(n => n.toLowerCase().includes(searchName.toLowerCase()));
    }

    // 5. Try if any sheet contains "blitz" (common Telstra report naming)
    if (!targetSheetName) {
      targetSheetName = sheetNames.find(n => n.toLowerCase().includes('blitz'));
    }
    
    if (!targetSheetName) {
      warnings.push(`Requested sheet "${options.sheetName}" not found in workbook. Available sheets: [${sheetNames.join(', ')}]. Falling back to index ${options?.sheetIndex ?? 0}.`);
    }
  }
  
  // Fallback to sheet index
  if (!targetSheetName && options?.sheetIndex != null && sheetNames[options.sheetIndex]) {
    targetSheetName = sheetNames[options.sheetIndex];
  }
  
  // Final fallback to first sheet
  if (!targetSheetName) {
    targetSheetName = sheetNames[0];
    warnings.push(`Using first sheet "${targetSheetName}" as fallback.`);
  }
  
  const ws = wb.Sheets[targetSheetName];
  if (!ws) {
    throw new Error(`Sheet "${targetSheetName}" could not be read from the workbook.`);
  }
  
  const aoa: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
  
  // Validate we got data
  if (!aoa || aoa.length === 0) {
    throw new Error(`Sheet "${targetSheetName}" appears to be empty.`);
  }
  
  // Header Detection
  let headerRowIdx = -1;
  let colMap: Record<keyof typeof DEFAULT_COLS, number> = {} as any;
  
  // Try to find header in first 20 rows
  for(let i=0; i<Math.min(aoa.length, 20); i++) {
    const row = (aoa[i] || []).map(c => String(c).toLowerCase().trim());
    // Check for critical columns presence
    const hasAccount = row.some(c => c.includes('account') && c.includes('number'));
    const hasMobile = row.some(c => c.includes('service number') || c.includes('mobile number'));
    
    if (hasAccount && hasMobile) {
      headerRowIdx = i;
      break;
    }
  }
  
  if (headerRowIdx === -1) {
    warnings.push('Could not detect header row (looked for "Account Number" and "Service Number/Mobile Number"). Using default column mapping (may be inaccurate).');
    // Use default mapping
    for(const [k, v] of Object.entries(DEFAULT_COLS)) {
      colMap[k as keyof typeof DEFAULT_COLS] = COL_IDX(v);
    }
  } else {
    const row = (aoa[headerRowIdx] || []).map(c => String(c).toLowerCase().trim());
    
    // Map columns
    for(const [k, keywords] of Object.entries(HEADER_KEYWORDS)) {
       const key = k as keyof typeof DEFAULT_COLS;
       let idx = -1;
       
       // Try exact match first
       for(const kw of keywords) {
         idx = row.findIndex(c => c === kw);
         if (idx !== -1) break;
       }
       
       // Try partial match if not found
       if (idx === -1) {
         for(const kw of keywords) {
           idx = row.findIndex(c => c.includes(kw));
           if (idx !== -1) break;
         }
       }
       
       if (idx !== -1) {
         colMap[key] = idx;
       } else {
         // Fallback to default if not found
         colMap[key] = COL_IDX(DEFAULT_COLS[key]);
         // Only warn for critical columns or if we are very unsure
         // warnings.push(`Could not find column for "${key}". Using default.`);
       }
    }
  }

  // Extract monthly series column indices (12 consecutive columns from each start)
  const MONTHLY_COUNT = 12;
  const monthlySeriesStart = {
    dataMb: colMap.monthlyDataMbStart,
    voiceCalls: colMap.monthlyVoiceCallsStart,
    voiceMinutes: colMap.monthlyVoiceMinutesStart,
    sms: colMap.monthlySmsStart,
    billTotal: colMap.monthlyBillTotalStart,
    overseasDataCalls: colMap.monthlyOverseasDataCallsStart,
    overseasDataMinutes: colMap.monthlyOverseasDataMinutesStart,
    overseasDataMb: colMap.monthlyOverseasDataMbStart,
    overseasDataExcess: colMap.monthlyOverseasDataExcessStart,
  };

  // Extract month labels from header row (e.g. "Jan 2026 Domestic Data Usage (MB)" -> "Jan 2026")
  const monthlyLabels: string[] = [];
  if (headerRowIdx !== -1) {
    const headerRow = aoa[headerRowIdx] || [];
    for (let m = 0; m < MONTHLY_COUNT; m++) {
      const cell = normStr(getCell(headerRow, monthlySeriesStart.billTotal + m));
      const match = cell.match(/^((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4})/i)
        || cell.match(/^(bill\s+total\s+)((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4})/i);
      if (match) {
        monthlyLabels.push(match[match.length === 3 ? 2 : 1]);
      } else {
        // Try to extract from the data usage column headers instead
        const dataCell = normStr(getCell(headerRow, monthlySeriesStart.dataMb + m));
        const dataMatch = dataCell.match(/((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4})/i);
        monthlyLabels.push(dataMatch ? dataMatch[1] : `Month ${m + 1}`);
      }
    }
  }

  // Determine start index (header + 1)
  const startIdx = headerRowIdx !== -1 ? headerRowIdx + 1 : Math.max(1, aoa.findIndex(r => normStr(r[COL_IDX(DEFAULT_COLS.accountNumber)]) && looksLikeAuMobile(normStr(r[COL_IDX(DEFAULT_COLS.mobileNumber)]))));

  const items: TelstraMobileLineItem[] = [];
  let blankUserNames = 0;

  for (let i = startIdx; i < aoa.length; i++) {
    const r = aoa[i] || [];
    
    // Use mapped columns with max string lengths to match backend constraints
    const mobileNumber = truncStr(getCell(r, colMap.mobileNumber), 50);
    if (!mobileNumber) continue;

    const deviceType = truncStr(getCell(r, colMap.deviceType), 100);
    const sim = truncStr(getCell(r, colMap.simNumber), 100);
    const imei = truncStr(getCell(r, colMap.deviceImei), 100);
    
    const isMobile = deviceType.toLowerCase().includes('mobile') || deviceType.toLowerCase().includes('handset') || sim || imei || looksLikeAuMobile(mobileNumber);
    if (!isMobile) continue;

    // Apply max string lengths to match backend C# StringLength constraints
    const accountNumber = truncStr(getCell(r, colMap.accountNumber), 100);
    
    // Skip rows with missing required fields
    if (!accountNumber || !mobileNumber) {
      // Only warn if we haven't spammed warnings yet
      if (warnings.length < 5) {
        warnings.push(`Row ${i+1}: Skipping row with missing AccountNumber or MobileNumber`);
      }
      continue;
    }

    const userName = truncStr(getCell(r, colMap.userName), 200);
    if (!userName) blankUserNames += 1;
    const planName = truncStr(getCell(r, colMap.planName), 200);
    
    // Use Device Name (column AH) for display - more user-friendly than model number (AG)
    const deviceName = truncStr(getCell(r, colMap.deviceName), 200);

    const line: TelstraMobileLineItem = {
      accountNumber,
      mobileNumber, // Already truncated to 50 chars above
      userName: userName || 'Unknown',
      serviceActivationDate: toDateString(getCell(r, colMap.serviceActivationDate)),
      planName: planName || 'Unknown Plan',
      monthlyFee: money(getCell(r, colMap.monthlyFee)),
      includedData: numberMb(getCell(r, colMap.includedData)),
      simNumber: sim, // Already truncated to 100 chars above
      deviceImei: imei || undefined, // Already truncated to 100 chars above
      deviceModel: deviceName || undefined, // Already truncated to 200 chars above
      deviceModelNumber: truncStr(getCell(r, colMap.deviceModelNumber), 100) || undefined,
      deviceMake: truncStr(getCell(r, colMap.deviceMake), 100) || undefined,
      deviceOperatingSystem: truncStr(getCell(r, colMap.deviceOperatingSystem), 100) || undefined,
      deviceCategory: truncStr(getCell(r, colMap.deviceCategory), 100) || undefined,
      deviceType: deviceType || undefined, // Already truncated to 100 chars above
      deviceAgeMonths: toFloat(getCell(r, colMap.deviceAgeMonths)) || undefined,
      latestMonthDataMb: numberMb(getCell(r, colMap.latestMonthDataMb)),
      latestMonthVoiceCalls: toInt(getCell(r, colMap.latestMonthVoiceCalls)),
      latestMonthVoiceMinutes: toInt(getCell(r, colMap.latestMonthVoiceMinutes)),
      latestMonthSms: toInt(getCell(r, colMap.latestMonthSms)),
      latestMonthBillTotal: money(getCell(r, colMap.latestMonthBillTotal)),
      avg3mDataMb: numberMb(getCell(r, colMap.avg3mDataMb)),
      avg3mVoiceMinutes: toInt(getCell(r, colMap.avg3mVoiceMinutes)),
      avg3mBill: money(getCell(r, colMap.avg3mBill)),
      avg6mDataMb: numberMb(getCell(r, colMap.avg6mDataMb)),
      avg6mVoiceMinutes: toInt(getCell(r, colMap.avg6mVoiceMinutes)),
      avg6mBill: money(getCell(r, colMap.avg6mBill)),
      deviceTradeInValue: money(getCell(r, colMap.deviceTradeInValue)),
      barRoaming: normalizeBarStatus(getCell(r, colMap.barRoaming)),
      barIdd: normalizeBarStatus(getCell(r, colMap.barIdd)),
      // IR (International Roaming)
      irDataPackCode: truncStr(getCell(r, colMap.irDataPackCode), 50) || undefined,
      irDataPackDescription: truncStr(getCell(r, colMap.irDataPackDescription), 200) || undefined,
      irDataPackMonthlyFee: money(getCell(r, colMap.irDataPackMonthlyFee)) || undefined,
      irVoicePackCode: truncStr(getCell(r, colMap.irVoicePackCode), 50) || undefined,
      irVoicePackDescription: truncStr(getCell(r, colMap.irVoicePackDescription), 200) || undefined,
      irVoicePackMonthlyFee: money(getCell(r, colMap.irVoicePackMonthlyFee)) || undefined,
      irActive: normalizeBarStatus(getCell(r, colMap.irActive)) || undefined,
      avg3mIrDataMb: numberMb(getCell(r, colMap.avg3mIrDataMb)) || undefined,
      // IDD (International Direct Dialing)
      iddPackIndicator: truncStr(getCell(r, colMap.iddPackIndicator), 50) || undefined,
      iddPackCode: truncStr(getCell(r, colMap.iddPackCode), 50) || undefined,
      iddPackDescription: truncStr(getCell(r, colMap.iddPackDescription), 200) || undefined,
      iddPackMonthlyFee: money(getCell(r, colMap.iddPackMonthlyFee)) || undefined,
      avg3mIddExcess: money(getCell(r, colMap.avg3mIddExcess)) || undefined,
      avg6mIddExcess: money(getCell(r, colMap.avg6mIddExcess)) || undefined,
      // Stale service flags
      noUsage3m: normalizeBarStatus(getCell(r, colMap.noUsage3m)) || undefined,
      noUsage6m: normalizeBarStatus(getCell(r, colMap.noUsage6m)) || undefined,
      noNetworkActivity3m: normalizeBarStatus(getCell(r, colMap.noNetworkActivity3m)) || undefined,
      noNetworkActivity6m: normalizeBarStatus(getCell(r, colMap.noNetworkActivity6m)) || undefined,
      onlyDiversionUsage3m: normalizeBarStatus(getCell(r, colMap.onlyDiversionUsage3m)) || undefined,
      onlyDiversionUsage6m: normalizeBarStatus(getCell(r, colMap.onlyDiversionUsage6m)) || undefined,
      onlyMessagebankDiversionUsage3m: normalizeBarStatus(getCell(r, colMap.onlyMessagebankDiversionUsage3m)) || undefined,
      onlyMessagebankDiversionUsage6m: normalizeBarStatus(getCell(r, colMap.onlyMessagebankDiversionUsage6m)) || undefined,
      // Location
      mostUsedSite: truncStr(getCell(r, colMap.mostUsedSite), 200) || undefined,
      mostUsedPostcode: truncStr(getCell(r, colMap.mostUsedPostcode), 20) || undefined,
      // Monthly history (12 months, newest first)
      monthlyLabels: monthlyLabels.length > 0 ? monthlyLabels : undefined,
      monthlyDataMb: Array.from({ length: MONTHLY_COUNT }, (_, m) => numberMb(getCell(r, monthlySeriesStart.dataMb + m))),
      monthlyVoiceCalls: Array.from({ length: MONTHLY_COUNT }, (_, m) => toInt(getCell(r, monthlySeriesStart.voiceCalls + m))),
      monthlyVoiceMinutes: Array.from({ length: MONTHLY_COUNT }, (_, m) => toInt(getCell(r, monthlySeriesStart.voiceMinutes + m))),
      monthlySms: Array.from({ length: MONTHLY_COUNT }, (_, m) => toInt(getCell(r, monthlySeriesStart.sms + m))),
      monthlyBillTotal: Array.from({ length: MONTHLY_COUNT }, (_, m) => money(getCell(r, monthlySeriesStart.billTotal + m))),
      monthlyOverseasDataCalls: Array.from({ length: MONTHLY_COUNT }, (_, m) => toInt(getCell(r, monthlySeriesStart.overseasDataCalls + m))),
      monthlyOverseasDataMinutes: Array.from({ length: MONTHLY_COUNT }, (_, m) => toFloat(getCell(r, monthlySeriesStart.overseasDataMinutes + m))),
      monthlyOverseasDataMb: Array.from({ length: MONTHLY_COUNT }, (_, m) => numberMb(getCell(r, monthlySeriesStart.overseasDataMb + m))),
      monthlyOverseasDataExcess: Array.from({ length: MONTHLY_COUNT }, (_, m) => money(getCell(r, monthlySeriesStart.overseasDataExcess + m))),
    };

    if (!Number.isFinite(line.monthlyFee as any)) {
       if (warnings.length < 10) warnings.push(`Row ${i+1}: monthlyFee not numeric`);
    }
    if (!Number.isFinite(line.latestMonthBillTotal as any)) {
       if (warnings.length < 10) warnings.push(`Row ${i+1}: latestMonthBillTotal not numeric`);
    }
    items.push(line);
  }

  const map = new Map<string, TelstraMobileLineItem>();
  for (const it of items) {
    const key = it.mobileNumber.replace(/\s/g,'');
    const prev = map.get(key);
    if (!prev || it.latestMonthBillTotal > prev.latestMonthBillTotal) map.set(key, it);
  }
  const rows = Array.from(map.values());

  const totals = {
    lines: rows.length,
    totalMonthlyFees: rows.reduce((s,x)=>s + (x.monthlyFee||0), 0),
    totalLatestMonthBills: rows.reduce((s,x)=>s + (x.latestMonthBillTotal||0), 0),
    totalLatestMonthDataMb: rows.reduce((s,x)=>s + (x.latestMonthDataMb||0), 0),
    totalIncludedDataMb: rows.reduce((s,x)=>s + (x.includedData||0), 0),
  };

  // Extract customer name from column D (should be same for all rows)
  let customerName = '';
  for (let i = startIdx; i < Math.min(aoa.length, startIdx + 5); i++) {
    const r = aoa[i] || [];
    const name = normStr(getCell(r, colMap.customerName));
    if (name && name.length > 0) {
      customerName = name;
      break;
    }
  }

  const statementMonth = (() => {
    const fromHeader = monthLabelToStatementMonth(monthlyLabels[0]);
    if (fromHeader) return fromHeader;
    const m = file.name.match(/(20\d{2})[-_]?(\d{2})/);
    if (m) return `${m[1]}-${m[2]}`;
    const d = new Date(file.lastModified || Date.now());
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  })();

  // Truncate sourceFilename to backend limit (300 chars)
  const sourceFilename = file.name.length > 300 ? file.name.slice(0, 300) : file.name;
  
  // Validate statementMonth format (YYYY-MM)
  if (!/^\d{4}-\d{2}$/.test(statementMonth)) {
    warnings.push(`Invalid statement month format: "${statementMonth}" (expected YYYY-MM)`);
  }

  const report: TelstraMobileReport = {
    tenantId,
    partnerId: '',
    sourceFilename,
    statementMonth,
    generatedAt: new Date().toISOString(),
    customerName, // Customer name extracted from column D
    rows,
    totals,
  };

  if (items.length > 0 && blankUserNames === items.length) {
    for (const item of items) item.userName = MISSING_USER_NAME;
  }
  return { report, warnings };
}
